// assets/byte-banner-slider.js

if (!customElements.get('byte-banner-slider')) {
  class ByteBannerSlider extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.scrollFrame = null;
      this.touchStartX = 0;
      this.isAutoplayPaused = false;
      this.isInteracting = false;
      this.isDragging = false;
      this.hasDragged = false;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleInteractionStart = this.handleInteractionStart.bind(this);
      this.boundHandleInteractionEnd = this.handleInteractionEnd.bind(this);
      this.boundHandleFocusOut = this.handleFocusOut.bind(this);
      this.boundHandleTouchStart = this.handleTouchStart.bind(this);
      this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);
      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerUp = this.handlePointerUp.bind(this);
      this.boundHandleDragClick = this.handleDragClick.bind(this);
      this.boundHandleDragStart = this.handleDragStart.bind(this);
      this.boundHandleBlockSelect = this.handleBlockSelect.bind(this);
      this.boundHandleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.track) return;

      // Tek slayt olsa bile link tıklaması çalışsın
      this.addEventListener('click', this.boundHandleClick);
      if (this.slides.length < 2) return;

      this.#bindEvents();
      this.#updateSlideState(0);
      this.#startAutoplay();
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#stopAutoplay();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-banner-slider-action]');

      if (!actionTrigger) {
        this.#openSlideLink(event);
        return;
      }

      const sliderAction = actionTrigger.dataset.bannerSliderAction;

      if (sliderAction === 'prev') this.previous();
      if (sliderAction === 'next') this.next();
      if (sliderAction === 'go-to') this.goTo(Number(actionTrigger.dataset.bannerSliderTarget));
      if (sliderAction === 'toggle-autoplay') this.toggleAutoplay();
    }

    handleScroll() {
      if (this.isFadeEffect) return;
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

      this.scrollFrame = requestAnimationFrame(() => {
        const slideWidth = this.track.clientWidth;
        if (!slideWidth) return;

        const visibleIndex = Math.round(Math.abs(this.track.scrollLeft) / slideWidth);
        if (visibleIndex !== this.currentIndex) this.#updateSlideState(visibleIndex);
      });
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') this.previous();
      if (event.key === 'ArrowRight') this.next();
    }

    handleInteractionStart() {
      this.isInteracting = true;
      this.#stopAutoplay();
    }

    handleInteractionEnd() {
      this.isInteracting = false;
      this.#startAutoplay();
    }

    handleFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;

      this.handleInteractionEnd();
    }

    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX;
    }

    handleTouchEnd(event) {
      const swipeDistance = event.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(swipeDistance) < 50) return;

      if (swipeDistance < 0) this.next();
      else this.previous();
    }

    handlePointerDown(event) {
      // Touch'ta native scroll / swipe çalışıyor, drag sadece mouse için
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.track.scrollLeft;

      window.addEventListener('pointermove', this.boundHandlePointerMove);
      window.addEventListener('pointerup', this.boundHandlePointerUp);
      window.addEventListener('pointercancel', this.boundHandlePointerUp);
    }

    handlePointerMove(event) {
      if (!this.isDragging) return;

      const dragDistance = event.clientX - this.dragStartX;

      // Küçük el titremesi tıklama sayılır, sürükleme değil
      if (!this.hasDragged && Math.abs(dragDistance) <= 8) return;

      if (!this.hasDragged) {
        this.hasDragged = true;
        this.classList.add('is-dragging');
      }

      if (!this.isFadeEffect) {
        this.track.scrollLeft = this.dragStartScrollLeft - dragDistance;
      }
    }

    handlePointerUp(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.#unbindDragEvents();

      // Sürükleme olmadıysa normal tıklama: slayt kaydırma yok, link çalışır
      if (!this.hasDragged) return;

      const dragDistance = event.clientX - this.dragStartX;
      const dragThreshold = Math.min(80, this.track.clientWidth * 0.15);

      this.classList.remove('is-dragging');

      if (dragDistance < -dragThreshold) {
        this.goTo(this.currentIndex + 1);
      } else if (dragDistance > dragThreshold) {
        this.goTo(this.currentIndex - 1);
      } else {
        this.goTo(this.currentIndex);
      }
    }

    handleDragClick(event) {
      // Sürükleme sonrası slayttaki link / buton tıklamasını engelle
      if (!this.hasDragged) return;

      event.preventDefault();
      event.stopPropagation();
      this.hasDragged = false;
    }

    handleDragStart(event) {
      // Native görsel/link sürüklemesini kapat
      event.preventDefault();
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-banner-slider-slide]');
      if (!selectedSlide) return;

      this.isInteracting = true;
      this.#stopAutoplay();
      this.goTo(Number(selectedSlide.dataset.bannerSliderIndex), { isInstant: true });
    }

    handleBlockDeselect() {
      this.handleInteractionEnd();
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    previous() {
      this.goTo(this.currentIndex - 1);
    }

    goTo(targetIndex, { isInstant = false, shouldWrap = this.shouldLoop } = {}) {
      const lastIndex = this.slides.length - 1;
      let nextIndex = targetIndex;

      if (nextIndex > lastIndex) nextIndex = shouldWrap ? 0 : lastIndex;
      if (nextIndex < 0) nextIndex = shouldWrap ? lastIndex : 0;

      if (!this.isFadeEffect) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.track.scrollTo({
          left: nextIndex * this.track.clientWidth,
          behavior: isInstant || prefersReducedMotion ? 'auto' : 'smooth'
        });
      }

      this.#updateSlideState(nextIndex);
    }

    toggleAutoplay() {
      this.isAutoplayPaused = !this.isAutoplayPaused;

      if (this.isAutoplayPaused) this.#stopAutoplay();
      else this.#startAutoplay();

      this.#updateToggleState();
    }

    #cacheElements() {
      this.track = this.querySelector('[data-banner-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-banner-slider-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-banner-slider-dot]'));
      this.prevButton = this.querySelector('[data-banner-slider-prev]');
      this.nextButton = this.querySelector('[data-banner-slider-next]');
      this.toggleButton = this.querySelector('[data-banner-slider-toggle]');

      this.isFadeEffect = this.classList.contains('banner-slider--fade');
      this.shouldLoop = this.dataset.bannerSliderLoop === 'true';
      this.autoplayDelay = Number(this.dataset.bannerSliderAutoplayDelay) || 5000;
      this.canAutoplay =
        this.dataset.bannerSliderAutoplay === 'true' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.canAutoplay && this.toggleButton) this.toggleButton.hidden = true;
    }

    #bindEvents() {
      this.addEventListener('mouseenter', this.boundHandleInteractionStart);
      this.addEventListener('mouseleave', this.boundHandleInteractionEnd);
      this.addEventListener('focusin', this.boundHandleInteractionStart);
      this.addEventListener('focusout', this.boundHandleFocusOut);
      this.track.addEventListener('keydown', this.boundHandleKeydown);
      this.track.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.boundHandlePointerDown);
      this.track.addEventListener('click', this.boundHandleDragClick, true);
      this.track.addEventListener('dragstart', this.boundHandleDragStart);

      if (this.isFadeEffect) {
        this.track.addEventListener('touchstart', this.boundHandleTouchStart, { passive: true });
        this.track.addEventListener('touchend', this.boundHandleTouchEnd, { passive: true });
      }

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', this.boundHandleBlockSelect);
        this.addEventListener('shopify:block:deselect', this.boundHandleBlockDeselect);
      }
    }

    #startAutoplay() {
      if (!this.canAutoplay || this.isAutoplayPaused || this.isInteracting) return;

      this.#stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.currentIndex + 1, { shouldWrap: true });
      }, this.autoplayDelay);
    }

    #stopAutoplay() {
      if (!this.autoplayTimer) return;

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    #openSlideLink(event) {
      if (event.defaultPrevented) return;

      const linkedSlide = event.target.closest('[data-banner-slider-url]');
      if (!linkedSlide || !this.contains(linkedSlide)) return;

      // Gerçek link / buton / form elemanına tıklandıysa native davranış çalışsın
      const interactiveElement = event.target.closest(
        'a[href], button, input, select, textarea, label, summary, [contenteditable="true"]'
      );
      if (interactiveElement) return;

      const slideUrl = linkedSlide.dataset.bannerSliderUrl;
      if (!slideUrl) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        window.open(slideUrl, '_blank', 'noopener');
        return;
      }

      window.location.assign(slideUrl);
    }

    #updateSlideState(activeIndex) {
      this.currentIndex = activeIndex;

      for (const [slideIndex, slide] of this.slides.entries()) {
        const isActive = slideIndex === activeIndex;

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;

        // Sıradaki slaytın görsellerini önden yükle
        if (Math.abs(slideIndex - activeIndex) === 1) this.#preloadSlideImages(slide);
      }

      for (const [dotIndex, dot] of this.dots.entries()) {
        const isActive = dotIndex === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      }

      if (!this.shouldLoop) {
        if (this.prevButton) this.prevButton.disabled = activeIndex === 0;
        if (this.nextButton) this.nextButton.disabled = activeIndex === this.slides.length - 1;
      }
    }

    #preloadSlideImages(slide) {
      for (const lazyImage of slide.querySelectorAll('img[loading="lazy"]')) {
        const isRendered = lazyImage.checkVisibility
          ? lazyImage.checkVisibility()
          : lazyImage.offsetParent !== null;

        if (isRendered) lazyImage.loading = 'eager';
      }
    }

    #updateToggleState() {
      if (!this.toggleButton) return;

      const toggleLabel = this.isAutoplayPaused
        ? this.dataset.bannerSliderPlayLabel
        : this.dataset.bannerSliderPauseLabel;

      this.classList.toggle('is-paused', this.isAutoplayPaused);
      this.toggleButton.setAttribute('aria-label', toggleLabel);
    }

    #unbindDragEvents() {
      window.removeEventListener('pointermove', this.boundHandlePointerMove);
      window.removeEventListener('pointerup', this.boundHandlePointerUp);
      window.removeEventListener('pointercancel', this.boundHandlePointerUp);
    }

    #unbindEvents() {
      this.#unbindDragEvents();

      this.removeEventListener('click', this.boundHandleClick);
      this.removeEventListener('mouseenter', this.boundHandleInteractionStart);
      this.removeEventListener('mouseleave', this.boundHandleInteractionEnd);
      this.removeEventListener('focusin', this.boundHandleInteractionStart);
      this.removeEventListener('focusout', this.boundHandleFocusOut);
      this.removeEventListener('shopify:block:select', this.boundHandleBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.boundHandleBlockDeselect);

      if (!this.track) return;

      this.track.removeEventListener('keydown', this.boundHandleKeydown);
      this.track.removeEventListener('scroll', this.boundHandleScroll);
      this.track.removeEventListener('pointerdown', this.boundHandlePointerDown);
      this.track.removeEventListener('click', this.boundHandleDragClick, true);
      this.track.removeEventListener('dragstart', this.boundHandleDragStart);
      this.track.removeEventListener('touchstart', this.boundHandleTouchStart);
      this.track.removeEventListener('touchend', this.boundHandleTouchEnd);
    }
  }

  customElements.define('byte-banner-slider', ByteBannerSlider);
}